import { useEffect, useRef, useState } from 'react';
import type { BoardItem } from '@/lib/apiClient';
import { meTooState } from '@/lib/everyonesFeedback';

/**
 * Its own `fbt-` styles, declared here: a class in this app is live only while the component that declares it is mounted (there are no
 * CSS Modules), so a control that leaned on a neighbor's rules would be styled until somebody moved it.
 */
const css = `
  .fbt { position: relative; display: flex; flex-direction: column; gap: 4px; }
  .fbt-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .fbt-ok { font: var(--ew-fw-medium) 12.5px/1.35 var(--ew-font); color: var(--ew-green); }
  .fbt-muted { margin: 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .fbt-err { margin: 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-danger); }
`;

/** How long after a press a change of state is taken to be its answer, if it has not been answered yet (a slow link, a cold server). */
const PRESS_COUNTS_FOR_MS = 60_000;

export interface MeTooProps {
  item: Pick<BoardItem, 'id' | 'sentByMe' | 'following' | 'status' | 'addedByMerge'>;
  /** A few words about the item, so a list of "Me too" buttons is not a list of identical names to somebody using a screen reader. */
  about?: string;
  /** Said "me too" on this screen just now, so it can say "Added". Otherwise the person already followed it. */
  justAdded?: boolean;
  /** A follow or an undo for this item is on its way: the button is off so a double click cannot send it twice. */
  pending?: boolean;
  /** Why the last attempt did not go through, in the API's words. */
  error?: string | null;
  onMeToo: (id: number) => void;
  onUndo: (id: number) => void;
}

/**
 * What a person can do about one item on the board: say "me too", undo it, or nothing, and what has just happened.
 *
 * The same control is used on the "Everyone's feedback" tab and under each suggestion, so it behaves the same in both. It decides
 * nothing: `meTooState` says which of four things applies, and the API still refuses what it will not allow (a closed item, or one
 * that stopped being public a moment ago), which comes back as `error`.
 */
export default function MeTooControl({ item, about, justAdded = false, pending = false, error = null, onMeToo, onUndo }: MeTooProps) {
  const state = meTooState(item);
  // ⚠️ A FOLLOW AN ADMIN MADE is not a "me too" the person said, so it is not worded as one: it says why they follow the item, and the button that
  // takes it back is "Stop following", not "Undo". (The API undoes either the same way; only what the person is told differs.)
  const merged = item.addedByMerge === true;
  const label = (verb: string) => (about ? `${verb}: ${about}` : verb);
  // ⚠️ WHAT A PRESS DOES TO THE PAGE. Pressing "Me too" removes that button and puts "Undo" in its place (and the reverse), and a button that
  // leaves the page takes focus with it, to the top of the document. So once the answer is in, focus goes to the control that replaced it,
  // and a live region says what happened. That region is ONE hidden element that is always in the page: a status element created together
  // with its words is unreliably spoken by a screen reader (it has to be there first and then CHANGE), and the words on screen cannot be
  // it, because they belong to the branch that just appeared.
  const pressed = useRef<{ what: 'follow' | 'unfollow'; at: number; merged: boolean } | null>(null);
  const meTooRef = useRef<HTMLButtonElement>(null);
  const undoRef = useRef<HTMLButtonElement>(null);
  const closedRef = useRef<HTMLParagraphElement>(null);
  const focusNext = useRef<'undo' | 'meToo' | 'closed' | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [said, setSaid] = useState('');
  // A press counts while it is on its way, and for a while after: long enough for its answer to come back, short enough that a change much
  // later (somebody else follows it, say) is not taken for that answer. It does NOT rest on `pending` alone, because whether "on its way"
  // ends before or after the new state arrives is up to the order the data layer notifies in, and the page must not depend on that order.
  const livePress = () => (pressed.current && (pending || Date.now() - pressed.current.at < PRESS_COUNTS_FOR_MS) ? pressed.current.what : null);

  // The state changed. If it is what the press asked for: say so, and put focus on what replaced the button that was pressed.
  useEffect(() => {
    const did = livePress();
    const of = about ? ` for ${about}` : '';
    if (did === 'follow' && state === 'following') { pressed.current = null; setSaid(`Me too added${of}.`); focusNext.current = 'undo'; }
    // Undone: an open item offers "Me too" again; a closed one has nothing to offer, so focus goes to the line that says so.
    else if (did === 'unfollow' && (state === 'can' || state === 'closed')) {
      // What was pressed, not what the item reads as now: once the follow is gone it no longer says it was added by a merge.
      const wasMerged = pressed.current?.merged === true;
      pressed.current = null; setSaid(`${wasMerged ? 'Stopped following' : 'Me too undone'}${of}.`); focusNext.current = state === 'can' ? 'meToo' : 'closed';
    }
    else if (!did) setSaid(''); // it changed some other way (followed on another screen, undone elsewhere): nothing was pressed here
  // eslint-disable-next-line react-hooks/exhaustive-deps -- it is the state changing that this answers
  }, [state]);

  // A refusal: the state does not change and the button comes back, and a button that was off let go of focus while it was.
  useEffect(() => {
    const did = livePress();
    if (!did || !error) return;
    pressed.current = null;
    focusNext.current = did === 'follow' ? 'meToo' : 'undo';
  // eslint-disable-next-line react-hooks/exhaustive-deps -- it is the error arriving that this answers
  }, [error]);

  // Focus goes where the effects above decided, but only once the button is ON: a button that is off cannot take focus, and whether the new
  // state or the end of "on its way" arrives first is not for the page to depend on. So this looks after every render until it has been done.
  // ⚠️ AND ONLY IF FOCUS WAS LOST OR IS STILL HERE. An answer can take a while, and somebody who has gone back to typing their message in the
  // meantime must not have focus pulled out from under them: the next space or Enter would press Undo.
  useEffect(() => {
    if (!focusNext.current || pending) return;
    const where = document.activeElement;
    const lostOrHere = !where || where === document.body || !!rootRef.current?.contains(where);
    if (lostOrHere) ({ undo: undoRef, meToo: meTooRef, closed: closedRef })[focusNext.current].current?.focus();
    focusNext.current = null;
  });
  const press = (what: 'follow' | 'unfollow', send: () => void) => { pressed.current = { what, at: Date.now(), merged }; setSaid(''); send(); };
  return (
    <div className="fbt" ref={rootRef}>
      <style>{css}</style>
      <span className="ew-sr-only" role="status">{said}</span>
      {state === 'sent' && <p className="fbt-muted">You sent this.</p>}

      {state === 'following' && (
        <div className="fbt-row">
          <span className="fbt-ok">
            {merged
              ? `You’re following this because a request you follow was combined into it.${item.status === 'open' ? ' You’ll be told when it’s answered.' : ''}`
              : justAdded
                ? 'Added. You’ll be told when it’s answered.'
                : item.status === 'open' ? 'You’re following this. You’ll be told when it’s answered.' : 'You’re following this.'}
          </span>
          <button
            type="button" className="btn btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }}
            ref={undoRef} disabled={pending} aria-label={label(merged ? 'Stop following' : 'Undo me too')} onClick={() => press('unfollow', () => onUndo(item.id))}
          >
            {pending ? (merged ? 'Stopping…' : 'Undoing…') : merged ? 'Stop following' : 'Undo'}
          </button>
        </div>
      )}

      {state === 'can' && (
        <div className="fbt-row">
          <button
            type="button" className="btn btn-primary" style={{ padding: '4px 12px' }}
            ref={meTooRef} disabled={pending} aria-label={label('Me too')} onClick={() => press('follow', () => onMeToo(item.id))}
          >
            {pending ? 'Adding…' : 'Me too'}
          </button>
          <span className="fbt-muted">You’ll be told when it’s answered.</span>
        </div>
      )}

      {state === 'closed' && <p className="fbt-muted" ref={closedRef} tabIndex={-1}>This one is closed, so there is nothing to add.</p>}

      {error && <p className="fbt-err" role="alert">{error}</p>}
    </div>
  );
}
